import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SiteLayout } from '../components/layout/SiteLayout';
import { PageSeo } from '../components/seo/PageSeo';
import { useCollection, PAGE_SIZE } from '../hooks/useCollection';
import type { CollectionEntry } from '../types/cms';
import { TemplateBlockRenderer } from '../components/TemplateBlockRenderer';

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

class TemplateErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="py-8 text-center text-sm text-zinc-400">
          Template rendering error. Check that only listing-compatible blocks are used.
        </div>
      );
    }
    return this.props.children;
  }
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function EntryCard({ entry, typeSlug }: { entry: CollectionEntry; typeSlug: string }) {
  const data = entry.dataJson ?? {};
  const imageUrl =
    (data.imageUrl as string | undefined) ??
    (data.image as string | undefined) ??
    (data.cover as string | undefined);
  const rawExcerpt =
    (data.excerpt as string | undefined) ??
    (data.summary as string | undefined) ??
    (data.description as string | undefined);
  const excerpt = rawExcerpt ? stripHtml(rawExcerpt) : undefined;
  const path = `/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;

  return (
    <Link
      to={path}
      className="block bg-white border border-zinc-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {imageUrl && (
        <div className="aspect-video overflow-hidden bg-zinc-100">
          <img src={imageUrl} alt={entry.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4 space-y-1.5">
        <h3 className="font-semibold text-zinc-900 text-sm leading-snug line-clamp-2">{entry.title}</h3>
        {excerpt && <p className="text-xs text-zinc-500 line-clamp-3">{excerpt}</p>}
        {entry.publishedAt && (
          <p className="text-[10px] text-zinc-400">
            {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        )}
      </div>
    </Link>
  );
}

export function CollectionListPage() {
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useCollection(typeSlug, page);

  const entries = data?.entries ?? [];
  const total = data?.total ?? entries.length;
  const collectionName = data?.contentType?.name ?? '';
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!typeSlug) return null;

  return (
    <SiteLayout>
      {collectionName && (
        <PageSeo title={collectionName} />
      )}

      <div className="py-8 px-4">
        <div className="mb-6 space-y-1">
          {collectionName && (
            <h1 className="text-2xl font-bold text-zinc-900">{collectionName}</h1>
          )}
          {!isLoading && total > 0 && (
            <p className="text-sm text-zinc-400">
              {total} {total === 1 ? 'entry' : 'entries'}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <svg className="w-6 h-6 animate-spin text-zinc-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
            </svg>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
            <p className="text-sm text-zinc-500">Failed to load entries. Please try again.</p>
          </div>
        ) : data?.listingTemplate ? (
          <TemplateErrorBoundary>
            <TemplateBlockRenderer blocks={data.listingTemplate.blocks} />
          </TemplateErrorBoundary>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
            <p className="text-sm text-zinc-500">No entries published yet.</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {entries.map((entry) => (
                <EntryCard key={entry.id} entry={entry} typeSlug={typeSlug} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-zinc-500">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}
